import slugify from '@sindresorhus/slugify'
import { type ClassValue, clsx } from 'clsx'
import { format as formatDateFns, parseISO } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { enUS, zhCN } from 'date-fns/locale'
import { twMerge } from 'tailwind-merge'

/**
 * Utility function to merge Tailwind CSS classes with CSS modules
 * Combines clsx for conditional classes and tailwind-merge for deduplication
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Utility function specifically for CSS modules
 * Combines CSS module classes without Tailwind merging
 */
export function cvm(...inputs: ClassValue[]) {
  return clsx(inputs)
}

/**
 * Format a date string or Date using date-fns with a consistent timezone.
 * Defaults to America/Chicago for display consistency across pages.
 */
export function formatDate(
  dateInput: string | Date | null,
  locale: string = 'en',
  timeZone: string = 'America/Chicago'
): string {
  const formatStr = locale === 'zh' ? 'yyyy年M月d日' : 'MMMM d, yyyy'
  if (!dateInput)
    return formatDateFns(new Date(), formatStr, {
      locale: locale === 'zh' ? zhCN : enUS,
    })
  const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput
  const zoned = toZonedTime(date, timeZone)

  return formatDateFns(zoned, formatStr, {
    locale: locale === 'zh' ? zhCN : enUS,
  })
}

/**
 * Parse an ISO string into a Date only if the input isn't already a Date.
 */
export function parseISOIfNotDateObject(dateInput: string | Date): Date {
  return typeof dateInput === 'string' ? parseISO(dateInput) : dateInput
}

/**
 * Convert a string to Title Case
 * Handles special cases like "AI", "API", "UI", "UX", etc.
 */
export function toTitleCase(text: string): string {
  // Special words that should be uppercase
  const specialWords = [
    'AI',
    'ML',
    'LLM',
    'OCR',
    'API',
    'UI',
    'UX',
    'CSS',
    'HTML',
    'JS',
    'TS',
    'GraphQL',
  ]

  return text
    .toLowerCase()
    .split(/[\s-]+/)
    .map(word => {
      // Check if it's a special word
      const upperWord = word.toUpperCase()
      if (specialWords.includes(upperWord)) {
        return upperWord
      }
      // Otherwise capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

/**
 * Find a tag by its slug (case-insensitive, handles slugified versions)
 * @param slug - The slugified tag to search for
 * @param tags - Array of tag names to search through
 * @returns The original tag name in Title Case if found, or the slug converted to Title Case
 */
export function findTagBySlug(
  slug: string,
  tags: string[],
  locale: string
): string {
  if (locale === 'zh') {
    // For Chinese, we assume tags are not slugified and match directly
    const decodedSlug = decodeURIComponent(slug)
    const found = tags.find(tag => tag === decodedSlug)
    return found ? found : decodedSlug
  }
  const normalizedSlug = slug.toLowerCase()
  const found = tags.find(tag => slugify(tag) === normalizedSlug)
  return found ? toTitleCase(found) : toTitleCase(slug.replace(/-/g, ' '))
}
